import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Avatar, DueDate, PriorityFlag, StatusPill } from '@/components/Chips'
import { Icon } from '@/components/Icon'
import { BulkActions } from '@/features/work-items/BulkActions'
import type { WorkItem } from '@/types/work'

type Column = 'key' | 'title' | 'status' | 'assignee' | 'priority' | 'dueDate'

const PRIORITY_ORDER: Record<string, number> = { urgent: 0, high: 1, normal: 2, low: 3 }

/**
 * The table layout.
 *
 * `View::LAYOUTS` has allowed `table` since Phase 1 and nothing drew one, so a
 * saved view could name a rendering that did not exist. It is the layout for
 * comparing rather than reading: every field in a column, sortable, nothing
 * hidden behind a card.
 *
 * Sorting is client-side on purpose. The rows are already all here — the query
 * layer follows the cursor to the end — so a round trip per column would be
 * slower and would disagree with the count in the header while it was in
 * flight.
 */
export function TaskTable({
  items,
  asOf,
  onOpen,
  showProject = false,
}: {
  items: WorkItem[]
  asOf: number
  onOpen: (item: WorkItem) => void
  showProject?: boolean
}) {
  const { t } = useTranslation()
  const [sort, setSort] = useState<{ column: Column; ascending: boolean }>({
    column: 'key',
    ascending: true,
  })
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set())

  // A row that has left the table cannot stay selected, or the action bar
  // would act on something the person can no longer see.
  const present = new Set(items.map((item) => item.id))
  const chosen = [...selected].filter((id) => present.has(id))

  function toggle(id: string) {
    setSelected((current) => {
      const next = new Set(current)
      if (!next.delete(id)) next.add(id)

      return next
    })
  }

  const rows = useMemo(() => {
    const value = (item: WorkItem): string | number => {
      switch (sort.column) {
        case 'title':
          return item.title.toLowerCase()
        case 'status':
          return item.state?.name?.toLowerCase() ?? ''
        case 'assignee':
          return item.assignee?.name?.toLowerCase() ?? ''
        case 'priority':
          return PRIORITY_ORDER[item.priority] ?? 9
        case 'dueDate':
          // Undated sorts last either way: a task with no date is not "earliest".
          return item.dueDate === null ? Number.MAX_SAFE_INTEGER : Date.parse(item.dueDate)
        default:
          return item.key
      }
    }

    return [...items].sort((a, b) => {
      const left = value(a)
      const right = value(b)
      const order = left < right ? -1 : left > right ? 1 : 0

      return sort.ascending ? order : -order
    })
  }, [items, sort])

  if (items.length === 0) {
    return (
      <p className="border-line bg-surface text-ink-700 rounded-md border p-4 text-sm">
        {t('work.noItems')}
      </p>
    )
  }

  const columns: { id: Column; label: string; align?: 'end' }[] = [
    { id: 'key', label: t('view.key') },
    { id: 'title', label: t('work.task') },
    { id: 'status', label: t('work.status') },
    { id: 'assignee', label: t('work.assignee') },
    { id: 'priority', label: t('work.priority') },
    { id: 'dueDate', label: t('work.dueDate'), align: 'end' },
  ]

  const allChosen = chosen.length === rows.length && rows.length > 0

  return (
    <div className="border-line bg-surface rounded-md border">
      {chosen.length === 0 ? null : (
        <BulkActions ids={chosen} onClear={() => setSelected(new Set())} />
      )}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm">
          <thead>
            <tr className="border-line-strong text-ink-500 border-b font-mono text-[10px] tracking-widest uppercase">
              <th scope="col" className="w-9 ps-4 pe-1 py-2.5">
                <input
                  type="checkbox"
                  checked={allChosen}
                  aria-label={t('work.selectAll')}
                  onChange={() =>
                    setSelected(allChosen ? new Set() : new Set(rows.map((item) => item.id)))
                  }
                  className="align-middle"
                />
              </th>
              {columns.map((column) => (
                <th
                  key={column.id}
                  scope="col"
                  aria-sort={
                    sort.column === column.id
                      ? sort.ascending
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                  className={`px-4 py-2.5 font-medium ${column.align === 'end' ? 'text-end' : 'text-start'}`}
                >
                  <button
                    type="button"
                    onClick={() =>
                      setSort((current) => ({
                        column: column.id,
                        ascending: current.column === column.id ? !current.ascending : true,
                      }))
                    }
                    className="hover:text-ink-900 inline-flex items-center gap-1 uppercase"
                  >
                    {column.label}
                    {sort.column === column.id ? (
                      <Icon
                        name="chevron"
                        className={`size-2.5 ${sort.ascending ? '-rotate-90' : 'rotate-90'}`}
                      />
                    ) : null}
                  </button>
                </th>
              ))}
              {showProject ? (
                <th scope="col" className="px-4 py-2.5 text-start font-medium">
                  {t('work.project')}
                </th>
              ) : null}
            </tr>
          </thead>

          <tbody>
            {rows.map((item) => (
              <tr
                key={item.id}
                className={`border-line border-b last:border-0 ${
                  selected.has(item.id) ? 'bg-brand-50' : 'hover:bg-surface-2'
                }`}
              >
                <td className="w-9 ps-4 pe-1 py-2">
                  <input
                    type="checkbox"
                    checked={selected.has(item.id)}
                    aria-label={t('work.selectItem', { key: item.key })}
                    onChange={() => toggle(item.id)}
                    className="align-middle"
                  />
                </td>
                <td className="px-4 py-2">
                  <button
                    type="button"
                    onClick={() => onOpen(item)}
                    className="text-brand-600 font-mono text-[11px]"
                  >
                    {item.key}
                  </button>
                </td>
                <td className="text-ink-900 max-w-md truncate px-4 py-2">{item.title}</td>
                <td className="px-4 py-2">
                  <StatusPill
                    name={item.state?.name ?? null}
                    category={item.state?.category ?? null}
                    compact
                  />
                </td>
                <td className="px-4 py-2">
                  <span className="flex items-center gap-2">
                  <Avatar name={item.assignee?.name ?? null} color={item.assignee?.color} size="sm" />
                    <span className="text-ink-700 text-[13px]">
                      {item.assignee?.name ?? t('work.unassigned')}
                    </span>
                  </span>
                </td>
                <td className="px-4 py-2">
                  <span className="flex items-center gap-1.5">
                    <PriorityFlag priority={item.priority} />
                  <span className="text-ink-700 text-[13px]">{t(`priority.${item.priority}`)}</span>
                  </span>
                </td>
                <td className="px-4 py-2 text-end">
                  <DueDate date={item.dueDate} asOf={asOf} />
                </td>
                {showProject ? (
                  <td className="text-ink-500 px-4 py-2 font-mono text-[11px]">
                    {item.project?.key ?? '—'}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
